import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { makeSlots } from '../../../lib/utils';
import { SlotCell, SlotGridProps, SlotStatus } from '../../../types/Exams';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { formatDate, isWorkday, normalizeYearMonth, pad, toYMD } from './slotHelpers';

const statusStyle: Record<SlotStatus, string> = {
  available: 'bg-success text-white',
  taken: 'bg-danger text-white',
  closed: 'bg-secondary text-white',
};
type Props = Omit<SlotGridProps, 'dates'> & {
  /** Optional currentYear. If omitted, the grid deduces it from currentMonth relative to now. */
  currentYear?: number;
};
// const formatDate = (iso: string) =>
//   new Date(iso).toLocaleDateString('uk-UA', { weekday: 'short', day: '2-digit', month: '2-digit' });

// export const SlotGrid: React.FC<SlotGridProps> = ({
//   dates,
//   cells,
//   workStart = '09:30',
//   workEnd = '17:30',
//   stepMin = 15,
//   onCellClick,
//   currentMonth,
//   monthChange,
// }) => {
//   const { t } = useTranslation();
//   const times = useMemo(
//     () => makeSlots(workStart, workEnd, stepMin),
//     [workStart, workEnd, stepMin],
//   );
//   // Fast lookup: map[date][time] -> SlotCell
//   const byDateTime = useMemo(() => {
//     const map: Record<string, Record<string, SlotCell>> = {};
//     for (const c of cells) {
//       if (!map[c.date]) map[c.date] = {};
//       map[c.date][c.time] = c;
//     }
//     return map;
//   }, [cells]);

//   const statusLabel: Record<SlotStatus, string> = {
//     available: t('exams.statusAvailable'),
//     taken: t('exams.statusTaken'),
//     closed: t('exams.statusClosed'),
//   };
//   useEffect(() => {
//     console.log('currentMonth', currentMonth);
//   }, [currentMonth]);
//   const handleClick = (cell: SlotCell) => {
//     if (onCellClick && cell.by !== 'student') return onCellClick(cell);

//     // Default popup
//     Swal.fire({
//       title: `${cell.time} • ${cell.date}`,
//       html: `
//         <div style="text-align:left">
//           <p><b>Status:</b> ${statusLabel[cell.status]}</p>
//           ${cell.by ? `<p><b>By:</b> ${cell.by}</p>` : ''}
//           ${cell.note ? `<p><b>Note:</b> ${cell.note}</p>` : ''}
//         </div>
//       `,
//       icon: cell.status === 'available' ? 'success' : cell.status === 'taken' ? 'error' : 'info',
//       confirmButtonText: 'OK',
//     });
//   };

//   return (
//     <>
//       <div>
//         {' '}
//         <p>Current month: {currentMonth}</p>
//         <Button type="button" onClick={() => monthChange(currentMonth - 1)}>
//           Prev
//         </Button>
//         <Button type="button" onClick={() => monthChange(currentMonth + 1)}>
//           Next
//         </Button>
//       </div>
//       <div className="table-responsive" style={{ maxHeight: 600 }}>
//         <table className="table table-bordered table-sm align-middle">
//           <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
//             <tr>
//               <th style={{ position: 'sticky', left: 0, zIndex: 3, background: 'var(--bs-light)' }}>
//                 {t('exams.slotTime')}
//               </th>
//               {dates.map((d) => (
//                 <th key={d}>{formatDate(d)}</th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {times.map((t) => (
//               <tr key={t}>
//                 <th
//                   style={{ position: 'sticky', left: 0, zIndex: 1, background: 'white' }}
//                   className="fw-normal"
//                 >
//                   {t}
//                 </th>
//                 {dates.map((d) => {
//                   const cell = byDateTime[d]?.[t] ?? {
//                     date: d,
//                     time: t,
//                     status: 'available' as SlotStatus,
//                   };
//                   const cls = statusStyle[cell.status];
//                   const title =
//                     `${statusLabel[cell.status]}` +
//                     (cell.by ? ` • ${cell.by}` : '') +
//                     (cell.note ? ` • ${cell.note}` : '');
//                   return (
//                     <td key={`${d}_${t}`} className="p-1">
//                       <button
//                         type="button"
//                         className={`w-100 btn btn-sm ${cls}`}
//                         title={title}
//                         onClick={() => handleClick(cell)}
//                         style={{ whiteSpace: 'nowrap' }}
//                       >
//                         {statusLabel[cell.status]}
//                       </button>
//                     </td>
//                   );
//                 })}
//               </tr>
//             ))}
//           </tbody>
//         </table>

//         {/* Legend */}
//         <div className="d-flex gap-3 mt-2">
//           <span className="badge bg-success">{statusLabel.available}</span>
//           <span className="badge bg-danger">{statusLabel.taken}</span>
//           <span className="badge bg-secondary">{statusLabel.closed}</span>
//         </div>
//       </div>
//     </>
//   );
// };
export const SlotGrid: React.FC<Props> = ({
  // dates <- no longer needed
  cells,
  workStart = '09:30',
  workEnd = '17:30',
  stepMin = 15,
  onCellClick,
  currentMonth,
  currentYear, // optional
  monthChange,
}) => {
  const { t } = useTranslation();
  const times = useMemo(
    () => makeSlots(workStart, workEnd, stepMin),
    [workStart, workEnd, stepMin],
  );

  // Decide which month/year to render
  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1; // 1..12
  const baseYear = currentYear ?? nowYear;
  const { year, month } = useMemo(
    () => normalizeYearMonth(baseYear, currentMonth),
    [baseYear, currentMonth],
  );

  // Compute visible date list:
  // - If viewing current month: start from today
  // - Else: start from the 1st
  // - Always end at the last day of that month
  // - Include only workdays (Mon–Fri)
  const visibleDates: string[] = useMemo(() => {
    const start =
      year === nowYear && month === nowMonth
        ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
        : new Date(year, month - 1, 1);
    start.setHours(0, 0, 0, 0);

    const end = new Date(year, month, 0); // last day of month
    end.setHours(0, 0, 0, 0);

    const out: string[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      if (isWorkday(cursor)) out.push(toYMD(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, [year, month, nowYear, nowMonth]);

  // Fast lookup: map[date][time] -> SlotCell
  const byDateTime = useMemo(() => {
    const map: Record<string, Record<string, SlotCell>> = {};
    for (const c of cells) {
      if (!map[c.date]) map[c.date] = {};
      map[c.date][c.time] = c;
    }
    return map;
  }, [cells]);

  const statusLabel: Record<SlotStatus, string> = {
    available: t('exams.statusAvailable'),
    taken: t('exams.statusTaken'),
    closed: t('exams.statusClosed'),
  };


  const handleClick = (cell: SlotCell) => {
    if (onCellClick && cell.status !== 'taken') return onCellClick(cell);

    Swal.fire({
      title: `${cell.time} • ${cell.date}`,
      text: t('exams.statusText'),

      icon: cell.status === 'available' ? 'success' : cell.status === 'taken' ? 'error' : 'info',
      confirmButtonText: 'OK',
    });
  };

  // Month navigation
  const prev = () => monthChange(currentMonth - 1);
  const next = () => monthChange(currentMonth + 1);

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-2">
        <p className="m-0">
          {t('exams.currentMonthYear') ?? 'Current month'}: {pad(month)}/{year}
        </p>
        <Button type="button" onClick={prev}>
          {t('exams.monthPrev') ?? 'Prev'}
        </Button>
        <Button type="button" onClick={next}>
          {t('exams.monthNext') ?? 'Next'}
        </Button>
      </div>

      <div className="table-responsive custom-scrollbar" style={{ maxHeight: 400 }}>
        <table className="table table-sm align-middle">
          <thead className="" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr className='sticky-slot-time' >
              <th style={{ position: 'sticky', left: 0, zIndex: 3 }}>
                {t('exams.slotTime')}
              </th>
              {visibleDates.map((d) => (
                <th key={d}>{formatDate(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {times.map((t) => (
              <tr key={t}>
                <th
                  style={{ position: 'sticky', left: 0, zIndex: 1, background: 'white' }}
                  className="fw-normal"
                >
                  {t}
                </th>
                {visibleDates.map((d) => {
                  const cell = byDateTime[d]?.[t] ?? {
                    date: d,
                    time: t,
                    status: 'available' as SlotStatus,
                  };
                  const cls = statusStyle[cell.status];
                  const title =
                    `${statusLabel[cell.status]}` +
                    (cell.by ? ` • ${cell.by}` : '') +
                    (cell.note ? ` • ${cell.note}` : '');
                  return (
                    <td key={`${d}_${t}`} className="p-1">
                      <button
                        type="button"
                        className={`w-100 btn btn-sm ${cls}`}
                        title={title}
                        onClick={() => handleClick(cell)}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {statusLabel[cell.status]}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Legend */}
        <div className="d-flex gap-3 mt-3 mb-3">
          <span className="badge bg-success">{statusLabel.available}</span>
          <span className="badge bg-danger">{statusLabel.taken}</span>
          <span className="badge bg-secondary">{statusLabel.closed}</span>
        </div>
      </div>
    </>
  );
};
